import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../data";

const items = [
  { icon: Phone, label: "Telefon", value: CONTACT.phone, href: CONTACT.phoneHref },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: MapPin, label: "Adresă", value: CONTACT.address },
  { icon: Clock, label: "Program", value: CONTACT.schedule },
];

export const Contact = () => {
  return (
    <section id="contact" className="relative bg-ink py-20 lg:py-28 overflow-hidden">
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-start">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-brand font-semibold text-sm tracking-wide uppercase"
            >
              <span className="h-px w-8 bg-brand" />
              Contact
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-white"
            >
              Hai să discutăm despre afacerea ta.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-lg text-white/70 leading-relaxed max-w-lg"
            >
              {CONTACT.company} — {CONTACT.person}. Suntem alături de tine cu soluții
              contabile personalizate. Contactează-ne pentru o ofertă.
            </motion.p>

            <div className="mt-10 space-y-4">
              {items.map((it, i) => {
                const Icon = it.icon;
                const content = (
                  <div className="flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 hover:border-white/20 transition-colors">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm text-white/50">{it.label}</p>
                      <p className="mt-0.5 text-white font-medium">{it.value}</p>
                    </div>
                  </div>
                );
                return (
                  <motion.div
                    key={it.label}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.1 + i * 0.07 }}
                  >
                    {it.href ? (
                      <a href={it.href} data-testid={`contact-${it.label.toLowerCase()}`}>
                        {content}
                      </a>
                    ) : (
                      <div data-testid={`contact-${it.label.toLowerCase()}`}>{content}</div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-base font-semibold text-white shadow-subtle hover:bg-brand-hover hover:-translate-y-0.5 transition-all duration-300"
              >
                <Phone className="h-5 w-5" />
                Sună acum
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-base font-semibold text-white hover:bg-white hover:text-ink hover:-translate-y-0.5 transition-all duration-300"
              >
                Trimite email
                <ArrowUpRight className="h-5 w-5" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-[24px] overflow-hidden shadow-lift ring-1 ring-white/10 h-[420px] lg:h-full lg:min-h-[540px]"
          >
            <iframe
              title="Locație AN CONTAB AUDIT pe Google Maps"
              src={CONTACT.mapsEmbed}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
